import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import type { Theme } from './types';
import { applyTokens, flattenTokens } from './themeUtils';
import nanzaDark from '@themes/nanza-dark.json';
import nanzaLight from '@themes/nanza-light.json';
import oakDark from '@themes/oak-dark.json';
import oakLight from '@themes/oak-light.json';

/**
 * Themes registry. Add a new JSON to /themes and import it here to make it
 * selectable. The keys here are the values the GUI (when it lands) would
 * read.
 */
export const themes: Record<string, Theme> = {
  'nanza-dark': nanzaDark as Theme,
  'nanza-light': nanzaLight as Theme,
  'oak-dark': oakDark as Theme,
  'oak-light': oakLight as Theme,
};

interface ThemeContextValue {
  themeId: string;
  theme: Theme;
  setThemeId: (id: string) => void;
  available: { id: string; name: string }[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'nanza-theme-id';

export function ThemeProvider({
  children,
  initialThemeId = 'nanza-dark',
}: PropsWithChildren<{ initialThemeId?: string }>) {
  const [themeId, setThemeIdState] = useState<string>(() => {
    if (typeof window === 'undefined') return initialThemeId;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && themes[stored] ? stored : initialThemeId;
  });
  const theme = themes[themeId] ?? themes['nanza-dark'];

  const setThemeId = (id: string) => {
    if (!themes[id]) return;
    setThemeIdState(id);
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // localStorage might be unavailable (private mode, embedded contexts);
      // theme still works in-memory, just doesn't persist.
    }
  };

  // Apply tokens to :root any time the active theme changes. This is what
  // allows a future theme editor to mutate a JSON in memory and have every
  // CSS Module that references var(--color-*) update with no remount.
  useEffect(() => {
    applyTokens(flattenTokens(theme));
    // Tag the root with the theme id so component CSS can do theme-specific
    // tweaks via :root[data-theme="nanza-light"] .foo { ... } if ever needed.
    document.documentElement.setAttribute('data-theme', theme.id);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      themeId,
      theme,
      setThemeId,
      available: Object.values(themes).map((t) => ({ id: t.id, name: t.name })),
    }),
    [themeId, theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider />');
  return ctx;
}
